import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import enUS from 'date-fns/locale/en-US';
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';

const defaultPresets = [
  {
    label: 'Today',
    range: () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: 'Yesterday',
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 1)),
      endDate: endOfDay(subDays(new Date(), 1)),
    }),
  },
  {
    label: 'This Week',
    range: () => ({
      startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
      endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    label: 'Last Week',
    range: () => ({
      startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
    }),
  },
  {
    label: 'This Month',
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
  {
    label: 'Last Month',
    range: () => ({
      startDate: startOfMonth(subMonths(new Date(), 1)),
      endDate: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
];

const CashDateRangePicker = ({
  dateRange,
  onDateChange,
  placeholder = 'Select Date Range',
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasSelectedRange = Boolean(dateRange?.startDate && dateRange?.endDate);

  const staticRanges = useMemo(() => {
    return defaultPresets.map((preset) => ({
      label: preset.label,
      range: preset.range,
      isSelected: (range) => {
        if (!range?.startDate || !range?.endDate) return false;
        const definedRange = preset.range();
        return (
          format(range.startDate, 'yyyy-MM-dd') ===
            format(definedRange.startDate, 'yyyy-MM-dd') &&
          format(range.endDate, 'yyyy-MM-dd') ===
            format(definedRange.endDate, 'yyyy-MM-dd')
        );
      },
      color: '#000000',
    }));
  }, []);

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (ranges) => {
    const selection = ranges.selection;
    if (selection) {
      onDateChange({
        startDate: selection.startDate,
        endDate: selection.endDate,
        key: 'selection',
        color: '#000000',
      });
      // Close popper when range is completed
      if (
        selection.startDate &&
        selection.endDate &&
        selection.startDate.getTime() !== selection.endDate.getTime()
      ) {
        setIsOpen(false);
      }
    }
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onDateChange({
      startDate: null,
      endDate: null,
      key: 'selection',
      color: '#000000',
    });
    setIsOpen(false);
  };

  let labelText = placeholder;
  if (hasSelectedRange) {
    const isSameDay =
      format(dateRange.startDate, 'yyyy-MM-dd') ===
      format(dateRange.endDate, 'yyyy-MM-dd');
    labelText = isSameDay
      ? format(dateRange.startDate, 'dd MMM yyyy')
      : `${format(dateRange.startDate, 'dd MMM yyyy')} - ${format(
          dateRange.endDate,
          'dd MMM yyyy'
        )}`;
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
      <Button
        variant="outlined"
        onClick={handleToggle}
        startIcon={
          <CalendarMonthIcon
            sx={{
              fontSize: 18,
              color: '#000000',
            }}
          />
        }
        endIcon={
          hasSelectedRange ? (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                p: '2px',
                ml: 0.5,
                color: '#000000',
                '&:hover': {
                  color: '#000000',
                  bgcolor: 'rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          ) : null
        }
        sx={{
          textTransform: 'none',
          lineHeight: '1.5',
          borderColor: hasSelectedRange ? '#000000' : '#D1D5DB',
          color: '#000000',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          height: '40px',
          px: 1.8,
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: '#000000',
            backgroundColor: '#F9FAFB',
          },
        }}
      >
        {labelText}
      </Button>

      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={4}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E5E7EB',
              '& .rdrSelected, & .rdrInRange, & .rdrStartEdge, & .rdrEndEdge': {
                backgroundColor: '#000000 !important',
                color: '#ffffff !important',
              },
              '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span':
                {
                  color: '#ffffff !important',
                },
              '& .rdrDayToday .rdrDayNumber span:after': {
                backgroundColor: '#000000 !important',
              },
              '& .rdrStaticRangeSelected': {
                color: '#000000 !important',
                fontWeight: 700,
              },
              '& .rdrStaticRangeSelected .rdrStaticRangeLabel': {
                backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
              },
            }}
          >
            <DateRangePicker
              locale={enUS}
              onChange={handleSelect}
              months={2}
              direction="horizontal"
              showDateDisplay={false}
              showPreview={false}
              staticRanges={staticRanges}
              inputRanges={[]}
              ranges={[
                dateRange?.startDate
                  ? {
                      startDate: dateRange.startDate,
                      endDate: dateRange.endDate,
                      key: 'selection',
                      color: '#000000',
                    }
                  : {
                      startDate: new Date(),
                      endDate: new Date(),
                      key: 'selection',
                      color: '#000000',
                    },
              ]}
              rangeColors={['#000000']}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CashDateRangePicker;
